import { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router';
import { api } from '@/api/client';
import { useSession } from '@/stores/session';
import { useFormFactor } from '@/lib/formFactor';
import { Icon } from '@/components/Icon';
import s from './PasswordPage.module.css';

export function PasswordPage() {
  const navigate = useNavigate();
  const ff = useFormFactor();
  const shell = useOutletContext<{ setPhoneTabBarHidden?: (hidden: boolean) => void } | undefined>();
  const user = useSession((state) => state.user);
  const [minLength, setMinLength] = useState(8);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [focusedField, setFocusedField] = useState<number | null>(null);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const fields = useRef<(HTMLInputElement | null)[]>([]);
  const readonly = import.meta.env.VITE_READONLY !== '0';

  useEffect(() => {
    let active = true;
    void api.get<{ passwordMinLength: number }>('/api/v2/me/settings')
      .then((settings) => { if (active) setMinLength(settings.passwordMinLength); })
      .catch(() => undefined);
    return () => { active = false; };
  }, []);
  useEffect(() => {
    const viewport = window.visualViewport;
    const update = () => setKeyboardInset(viewport ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop) : 0);
    update();
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    return () => { viewport?.removeEventListener('resize', update); viewport?.removeEventListener('scroll', update); };
  }, []);
  useEffect(() => {
    if (ff !== 'phone') return;
    shell?.setPhoneTabBarHidden?.(focusedField !== null);
    return () => shell?.setPhoneTabBarHidden?.(false);
  }, [ff, focusedField, shell?.setPhoneTabBarHidden]);

  const longEnough = next.length >= minLength;
  const different = !!current && !!next && current !== next;
  const matches = !!next && confirm === next;
  const canSave = !readonly && !!current && longEnough && different && matches && !busy;
  const submit = async () => {
    if (!canSave) return;
    setBusy(true);
    setError('');
    try {
      await api.post('/api/v2/me/password', { currentPassword: current, newPassword: next });
      setCurrent(''); setNext(''); setConfirm(''); setDone(true);
    } catch (cause) { setError(cause instanceof Error ? cause.message : '保存失败，请重试。'); }
    finally { setBusy(false); }
  };

  return <div className={s.page}>
    <header className={s.header}><button type="button" onClick={() => navigate('/me')}>取消</button><h1>修改密码</h1><button type="button" disabled={!canSave} onClick={() => void submit()}>{busy ? '保存中…' : '保存'}</button></header>
    <main className={s.body}>
      <p className={s.identity}>{user?.name || '当前账号'} · {user?.username}</p>
      {done ? <div className={s.success} role="status"><b>密码已修改</b><p>其他设备需要用新密码重新登录；这台设备保持登录。</p><button type="button" onClick={() => navigate('/me')}>返回我的</button></div> : <>
        <div className={s.form}>
          <label>当前密码<input ref={(node) => { fields.current[0] = node; }} type="password" autoComplete="current-password" value={current} onFocus={() => setFocusedField(0)} onBlur={(event) => { const target = event.relatedTarget; if (!(target instanceof HTMLElement) || (!target.closest('[data-keyboard-toolbar]') && !target.matches('input'))) setFocusedField(null); }} onChange={(event) => setCurrent(event.target.value)} placeholder="请输入当前密码" /></label>
          <label>新密码<input ref={(node) => { fields.current[1] = node; }} type="password" autoComplete="new-password" value={next} onFocus={() => setFocusedField(1)} onBlur={(event) => { const target = event.relatedTarget; if (!(target instanceof HTMLElement) || (!target.closest('[data-keyboard-toolbar]') && !target.matches('input'))) setFocusedField(null); }} onChange={(event) => setNext(event.target.value)} placeholder={`至少 ${minLength} 位`} />
            <span className={`${s.rule} ${longEnough ? s.ok : ''}`}>{longEnough ? '✓' : '○'} 已满 {minLength} 位</span>
            <span className={`${s.rule} ${different ? s.ok : ''}`}>{different ? '✓' : '○'} 与当前密码不同</span>
          </label>
          <label>确认新密码<input ref={(node) => { fields.current[2] = node; }} type="password" autoComplete="new-password" value={confirm} onFocus={() => setFocusedField(2)} onBlur={(event) => { const target = event.relatedTarget; if (!(target instanceof HTMLElement) || (!target.closest('[data-keyboard-toolbar]') && !target.matches('input'))) setFocusedField(null); }} onChange={(event) => setConfirm(event.target.value)} placeholder="再输一次" />{confirm && !matches && <span className={s.mismatch}>两次输入不一致</span>}</label>
        </div>
        <div className={`${s.warning} ${s.phoneWarning}`}><Icon name="info" size={18} /><p><b>保存后，其他设备上的登录会失效</b><br />包括你的 iPad 和电脑，要用新密码重新登录。这台设备保持登录。</p></div>
        {readonly && <p className={s.notice}>当前为只读版本，密码修改暂不可提交。</p>}
        {error && <p className={s.error} role="alert">{error}</p>}
      </>}
    </main>
    {ff === 'phone' && focusedField !== null && <div className={s.keyboardToolbar} style={{ bottom: keyboardInset }} role="toolbar" aria-label="密码字段导航" data-keyboard-toolbar>
      <button type="button" disabled={focusedField === 0} onClick={() => fields.current[Math.max(0, focusedField - 1)]?.focus()}>上一项</button>
      <button type="button" disabled={focusedField === 2} onClick={() => fields.current[Math.min(2, focusedField + 1)]?.focus()}>下一项</button>
      <button type="button" className={s.doneKey} onClick={() => { fields.current[focusedField]?.blur(); setFocusedField(null); }}>完成</button>
    </div>}
  </div>;
}
